import clsx from 'clsx';
import React, { FC } from 'react';
import { twMerge } from 'tailwind-merge';

import Typography from '@/components/typography';

import { pickTextColorBasedOnBgColor } from '../../utils/pickColorBasedOnBgColor';

type ChipVariant = 'light' | 'medium' | 'dark' | 'darker';
export type ChipSize = 'small' | 'medium' | 'large';

type Props = {
  canRemove: boolean;
  onRemove?: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  onClick?: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  label: string;
  className?: string;
  variant: ChipVariant;
  color?: string;
  /** The color of the text and X inside the chip, if this is not provided a color will be chosen for you */
  textColor?: 'light' | 'dark';
  hoverTitle?: boolean;
  active?: boolean;
  disabled?: boolean;
  displayNumber?: number | string;
  displayNumberColor?: {
    background: string;
    text: string;
  };
  size?: ChipSize;
  labelClassName?: string;
};
function getColors(variant: ChipVariant) {
  switch (variant) {
    case 'medium':
      return 'bg-gray-10 border-gray-70 text-gray-70';
    case 'dark':
      return 'bg-gray-70 border-gray-70 text-white';
    case 'darker':
      return 'bg-gray-100 border-gray-100 text-white';
    default:
      return 'bg-white border-gray-70 text-gray-70';
  }
}

/** UI Library Chip */
const Chip: FC<Props> = ({
  canRemove,
  label,
  variant,
  active = true,
  className = '',
  color = '',
  disabled = false,
  displayNumber = undefined,
  displayNumberColor = undefined,
  hoverTitle = true,
  onClick = undefined,
  onRemove = () => {},
  textColor = undefined,
  size = 'medium',
  labelClassName = undefined,
}) => {
  const getChipTextColor = () => {
    // If there is a provided text color, user that, otherwise continue to color picking function
    if (textColor === 'light') {
      return 'text-white';
    }
    if (textColor === 'dark') {
      return 'text-gray-100';
    }

    if (!color) return 'text-gray-100';

    return pickTextColorBasedOnBgColor<String>(
      color,
      'text-white',
      'text-gray-100'
    );
  };

  const getChipClassNames = () => {
    const lightClassName = 'bg-gray-70/30 hover:bg-gray-70/80 hover:text-white';
    const darkClassName =
      'bg-gray-10/30 hover:bg-gray-10/80 hover:text-gray-100';

    // If a text coloris provided, that takes priority
    if (textColor === 'light') return lightClassName;
    if (textColor === 'dark') return darkClassName;

    if (!color) return 'bg-gray-10/30';

    return pickTextColorBasedOnBgColor<String>(
      color,
      darkClassName,
      lightClassName,
      150
    );
  };

  if (!active)
    return (
      <button
        className={clsx(
          'm-[4px] flex h-6 items-center gap-x-1 rounded-full pl-3 pr-3 transition-colors',
          'border-1 border-gray-70 text-gray-70 ',
          'disabled:border-gray-20 disabled:text-gray-20',
          clsx(!disabled && 'hover:bg-gray-70 hover:text-white'),
          clsx(onClick ? 'hover:cursor-pointer' : 'hover:cursor-default')
        )}
        type="button"
        onClick={onClick}
      >
        <Typography className="flex items-center" variant="h5">
          {label}
        </Typography>
      </button>
    );

  return (
    <button type="button" onClick={onClick}>
      <div
        className={twMerge(
          className,
          getColors(variant),
          'flex items-center gap-x-1 rounded-full p-2',
          canRemove ? 'pr-1' : 'pr-2',
          displayNumber
            ? ` ${size === 'large' && 'p-2'} ${
                size === 'medium' && 'px-2 py-1'
              }`
            : 'h-6'
        )}
        style={{ backgroundColor: color }}
      >
        {displayNumber && displayNumberColor && (
          <div
            className={twMerge(
              'h-6 w-6 rounded-full',
              `${displayNumberColor.background} ${displayNumberColor.text}`
            )}
          >
            <Typography>{displayNumber}</Typography>
          </div>
        )}
        {label && (
          <Typography
            className={clsx(
              getChipTextColor(),
              'max-w-[120px] truncate',
              labelClassName
            )}
            title={hoverTitle ? label : ''}
            variant="h5"
          >
            {label}
          </Typography>
        )}

        {canRemove && (
          <button
            className={clsx(
              'flex h-4 w-4 items-center justify-center rounded-full transition-colors',
              getChipClassNames(),
              getChipTextColor()
            )}
            type="button"
            onClick={onRemove}
          >
            <span
              className={clsx('material-icons-outlined !text-[8px] font-bold')}
            >
              close
            </span>
          </button>
        )}
      </div>
    </button>
  );
};

export default Chip;
